const styles = {
  INFO: "color: #3B82F6; font-weight: bold;",
  SUCCESS: "color: #10B981; font-weight: bold;",
  WARN: "color: #F59E0B; font-weight: bold;",
  ERROR: "color: #EF4444; font-weight: bold;"
};
const createLogEntry = (level, message, additionalData, includeStack = false) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  let logMessage;
  let logData = additionalData;
  if (typeof message === "string") {
    logMessage = message;
  } else if (typeof message === "object" && message !== null) {
    logMessage = "Object logged";
    logData = message;
  } else {
    logMessage = String(message);
  }
  const entry = {
    timestamp,
    level,
    message: logMessage
  };
  if (logData !== void 0) {
    entry.data = logData;
  }
  if (includeStack) {
    const stack = new Error().stack;
    if (stack) {
      entry.stack = stack.split("\n").slice(3).join("\n");
    }
  }
  if (typeof window !== "undefined") {
    entry.context = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };
  }
  return entry;
};
const logWithStyle = (consoleMethod, level, entry) => {
  console[consoleMethod](`%c[${level}]`, styles[level], entry);
};
const logger = {
  info: (message, data) => {
    const entry = createLogEntry("INFO", message, data);
    logWithStyle("log", "INFO", entry);
  },
  success: (message, data) => {
    const entry = createLogEntry("SUCCESS", message, data);
    logWithStyle("log", "SUCCESS", entry);
  },
  warn: (message, data) => {
    const entry = createLogEntry("WARN", message, data, true);
    logWithStyle("warn", "WARN", entry);
  },
  error: (message, data) => {
    const entry = createLogEntry("ERROR", message, data, true);
    logWithStyle("error", "ERROR", entry);
  }
};
function setCookie(name, value, minutes) {
  document.cookie = `${name}=${value};max-age=${minutes * 60};path=/`;
}
function getCookie(name) {
  const cookies = document.cookie.split(";");
  const foundCookie = cookies.find((cookie) => {
    const [cookieName] = cookie.split("=");
    return cookieName.trim() === name;
  });
  return foundCookie?.split("=")[1];
}
class StorageHelper {
  storage = null;
  type;
  isAvailable = false;
  constructor(type) {
    this.type = type;
    this.checkAvailability();
  }
  /**
   * Check if storage is available (handles private browsing, etc.)
   */
  checkAvailability() {
    try {
      const storage = window[this.type];
      const testKey = "__storage_test__";
      storage.setItem(testKey, "test");
      storage.removeItem(testKey);
      this.storage = storage;
      this.isAvailable = true;
    } catch (e) {
      this.isAvailable = false;
      logger.warn(`${this.type} is not available`, { error: e });
    }
  }
  /**
   * Set an item in storage
   */
  set(key, value, expiresInMinutes) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      const item = { value };
      if (expiresInMinutes) {
        item.expires = Date.now() + expiresInMinutes * 60 * 1e3;
      }
      this.storage.setItem(key, JSON.stringify(item));
      return true;
    } catch (e) {
      logger.error("Failed to set storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Get an item from storage
   */
  get(key) {
    if (!this.isAvailable || !this.storage) return null;
    try {
      const raw = this.storage.getItem(key);
      if (!raw) return null;
      const item = JSON.parse(raw);
      if (item.expires && Date.now() > item.expires) {
        this.remove(key);
        return null;
      }
      return item.value;
    } catch (e) {
      logger.error("Failed to get storage item", { key, error: e });
      return null;
    }
  }
  /**
   * Remove an item from storage
   */
  remove(key) {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.removeItem(key);
      return true;
    } catch (e) {
      logger.error("Failed to remove storage item", { key, error: e });
      return false;
    }
  }
  /**
   * Clear all items from storage
   */
  clear() {
    if (!this.isAvailable || !this.storage) return false;
    try {
      this.storage.clear();
      return true;
    } catch (e) {
      logger.error("Failed to clear storage", { error: e });
      return false;
    }
  }
  /**
   * Check if a key exists in storage
   */
  has(key) {
    if (!this.isAvailable || !this.storage) return false;
    return this.storage.getItem(key) !== null;
  }
  /**
   * Get all keys in storage
   */
  keys() {
    if (!this.isAvailable || !this.storage) return [];
    const keys = [];
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  }
  /**
   * Get storage size in bytes (approximate)
   */
  size() {
    if (!this.isAvailable || !this.storage) return 0;
    let total = 0;
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key) {
        const value = this.storage.getItem(key);
        if (value) {
          total += key.length + value.length;
        }
      }
    }
    return total;
  }
  /**
   * Create a namespaced storage helper
   * Prefixes all keys with the namespace
   */
  namespace(prefix) {
    return new NamespacedStorage(this, prefix);
  }
  /**
   * Check if storage is available
   */
  get available() {
    return this.isAvailable;
  }
}
class NamespacedStorage {
  constructor(storage, prefix) {
    this.storage = storage;
    this.prefix = prefix;
  }
  getKey(key) {
    return `${this.prefix}:${key}`;
  }
  set(key, value, expiresInMinutes) {
    return this.storage.set(this.getKey(key), value, expiresInMinutes);
  }
  get(key) {
    return this.storage.get(this.getKey(key));
  }
  remove(key) {
    return this.storage.remove(this.getKey(key));
  }
  has(key) {
    return this.storage.has(this.getKey(key));
  }
  /**
   * Clear all items in this namespace
   */
  clear() {
    const keys = this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`));
    let success = true;
    keys.forEach((key) => {
      if (!this.storage.remove(key)) {
        success = false;
      }
    });
    return success;
  }
  /**
   * Get all keys in this namespace (without prefix)
   */
  keys() {
    const prefixLength = this.prefix.length + 1;
    return this.storage.keys().filter((key) => key.startsWith(`${this.prefix}:`)).map((key) => key.substring(prefixLength));
  }
}
const local = new StorageHelper("localStorage");
new StorageHelper("sessionStorage");
const CONSENT_STORAGE_KEY$2 = "cookieSelections";
const isConsentGranted = () => {
  const selections = local.get(CONSENT_STORAGE_KEY$2);
  return selections?.analytics === true;
};
const idleInject = (inject) => {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(inject);
  } else {
    setTimeout(inject, 0);
  }
};
const buildGtmSrc = ({ containerId, serverUrl, scriptName }) => {
  if (serverUrl && scriptName) {
    const encodedId = btoa(`id=${containerId}`).replace(/=+$/, "");
    return `${serverUrl}/${scriptName}?P=${encodedId}&page=1`;
  }
  return `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
};
class ThemeScripts {
  /** Loads a script according to its strategy. TypeScript narrows to `GtmScriptConfig` when `id: 'gtm'`. */
  load = (config) => {
    if (config.id === "gtm") {
      this.loadGtm(config);
      return;
    }
    const { id, strategy, src, inline, async: isAsync = true, defer = false, attributes = {}, onLoad, onError } = config;
    this.applyStrategy({
      strategy,
      inject: () => this.inject({ id, src, inline, isAsync, defer, attributes, onLoad, onError })
    });
  };
  /** Handles GTM-specific setup: dataLayer push, URL building, noscript fallback. */
  loadGtm = (config) => {
    const { containerId } = config.gtmConfig;
    this.applyStrategy({
      strategy: config.strategy,
      inject: () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ "gtm.start": (/* @__PURE__ */ new Date()).getTime(), event: "gtm.js" });
        this.inject({
          id: "gtm",
          src: buildGtmSrc(config.gtmConfig),
          isAsync: true,
          defer: false,
          attributes: { "data-gtm": containerId },
          onLoad: config.onLoad,
          onError: config.onError
        });
        if (!document.querySelector(`noscript[data-gtm="${containerId}"]`)) {
          const noscript = document.createElement("noscript");
          noscript.dataset.gtm = containerId;
          const iframe = document.createElement("iframe");
          iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
          iframe.height = "0";
          iframe.width = "0";
          iframe.style.cssText = "display:none;visibility:hidden";
          noscript.appendChild(iframe);
          document.body.insertAdjacentElement("afterbegin", noscript);
        }
      }
    });
  };
  applyStrategy = ({ strategy, inject }) => {
    switch (strategy) {
      case "immediate": {
        inject();
        break;
      }
      case "on-default-consent-set": {
        window.addEventListener("edition:consent-default-set", () => idleInject(inject), { once: true });
        break;
      }
      case "on-consent-granted": {
        window.addEventListener(
          "edition:consent-default-set",
          () => {
            if (isConsentGranted()) {
              idleInject(inject);
              return;
            }
            const waitForConsent = () => {
              if (!isConsentGranted()) return;
              idleInject(inject);
              window.removeEventListener("edition:consent-updated", waitForConsent);
            };
            window.addEventListener("edition:consent-updated", waitForConsent);
          },
          { once: true }
        );
        break;
      }
      case "idle": {
        idleInject(inject);
        break;
      }
      default: {
        const _exhaustive = strategy;
        console.warn("[ThemeScripts] Unknown strategy:", _exhaustive);
      }
    }
  };
  inject = ({ id, src, inline, isAsync, defer, attributes, onLoad, onError }) => {
    if (document.querySelector(`script[data-script-id="${id}"]`)) {
      return;
    }
    if (inline) {
      inline();
    }
    if (!src) {
      return;
    }
    const script = document.createElement("script");
    script.async = isAsync;
    script.defer = defer;
    script.src = src;
    script.dataset.scriptId = id;
    Object.entries(attributes).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });
    if (onLoad) {
      script.addEventListener("load", onLoad);
    }
    if (onError) {
      script.addEventListener("error", onError);
    }
    document.head.appendChild(script);
  };
}
const LOG_STYLES = {
  header: "background:#4f46e5;color:#fff;padding:2px 8px;border-radius:3px;font-weight:bold",
  event: "background:#10b981;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  context: "background:#0ea5e9;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  consent: "background:#8b5cf6;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  init: "background:#f59e0b;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  config: "background:#6b7280;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold;margin-left:4px",
  label: "color:inherit;font-weight:bold;margin-left:4px",
  muted: "color:#6b7280;font-style:italic;margin-left:4px"
};
const CATEGORY_LABELS = {
  event: "Event",
  context: "Context",
  consent: "Consent",
  init: "Init",
  config: "Config"
};
const debugLog = ({ category, label, payload }) => {
  const timestamp = (/* @__PURE__ */ new Date()).toISOString();
  const displayTime = timestamp.split("T")[1].replace("Z", "");
  const categoryStyle = LOG_STYLES[category];
  console.groupCollapsed("%cEdition DataLayer%c%s%c%s%c%s", LOG_STYLES.header, categoryStyle, ` ${CATEGORY_LABELS[category]}`, LOG_STYLES.label, ` ${label}`, LOG_STYLES.muted, ` ${displayTime}`);
  if (payload !== void 0) console.log(payload);
  console.groupEnd();
};
const toAbsoluteUrl = (url) => {
  if (!url) {
    return null;
  }
  if (url.startsWith("//")) {
    return `https:${url}`;
  }
  if (url.startsWith("/")) {
    return `${window.location.origin}${url}`;
  }
  return url;
};
const CONSENT_STORAGE_KEY$1 = "cookieSelections";
const CONSENT_DENIED_DEFAULTS = { personalization: false, marketing: false, analytics: false };
const CONSENT_KEYS = ["personalization", "marketing", "analytics"];
class CookieBanner extends HTMLElement {
  consentApiReady = false;
  async connectedCallback() {
    await this.setupConsent();
    this.addEventListeners();
    this.activateTab("consent");
    this.syncCheckboxGroups();
    if (this.dataset.forceShow === "true" || await this.shouldShowBanner()) {
      this.showBanner();
    }
  }
  /** Sets gtag consent defaults, fires `edition:consent-default-set`, and optionally loads GTM. */
  setupConsent = async () => {
    this.ensureGtag();
    const selections = local.get(CONSENT_STORAGE_KEY$1) ?? CONSENT_DENIED_DEFAULTS;
    window.gtag?.("consent", "default", {
      functionality_storage: "granted",
      analytics_storage: selections.analytics ? "granted" : "denied",
      personalization_storage: selections.personalization ? "granted" : "denied",
      ad_personalization: selections.personalization ? "granted" : "denied",
      ad_storage: selections.marketing ? "granted" : "denied",
      ad_user_data: selections.marketing ? "granted" : "denied",
      wait_for_update: 3e3
    });
    window.gtag?.("set", "ads_data_redaction", !selections.marketing);
    window.gtag?.("set", "url_passthrough", true);
    window.dispatchEvent(new CustomEvent("edition:consent-default-set"));
    await this.loadShopifyConsentApi();
  };
  addEventListeners = () => {
    this.querySelector("#tab-consent")?.addEventListener("click", () => this.activateTab("consent"));
    this.querySelector("#tab-details")?.addEventListener("click", () => this.activateTab("details"));
    this.querySelector(".allow-all-btn")?.addEventListener("click", this.handleAllowAll);
    this.querySelector(".allow-selected-btn")?.addEventListener("click", this.handleAllowSelected);
    this.querySelector('[data-action="reject"]')?.addEventListener("click", this.handleRejectAll);
    document.getElementById("manage-cookie-consent")?.addEventListener("click", (event) => {
      event.preventDefault();
      this.showBanner();
    });
  };
  activateTab = (tab) => {
    ["consent", "details"].forEach((id) => {
      this.querySelector(`#tab-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("active", id === tab);
      this.querySelector(`#panel-${id}`)?.classList.toggle("tw:hidden!", id !== tab);
    });
  };
  /**
   * Returns true if the banner should be shown.
   * Prefers localStorage over the Shopify API — if the user has a stored selection we trust it
   * and skip the async API call entirely.
   */
  shouldShowBanner = async () => {
    if (local.get(CONSENT_STORAGE_KEY$1)) {
      return false;
    }
    if (!this.consentApiReady || !window.Shopify?.customerPrivacy?.currentVisitorConsent) {
      return true;
    }
    try {
      const { preferences, analytics, marketing } = await window.Shopify.customerPrivacy.currentVisitorConsent();
      return [preferences, analytics, marketing].every((v) => (v ?? "") === "");
    } catch {
      return true;
    }
  };
  loadShopifyConsentApi = () => {
    if (!window.Shopify?.loadFeatures) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      try {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], (error) => {
          if (error) {
            logger.error("Shopify consent-tracking-api failed to load", error);
            resolve();
            return;
          }
          this.consentApiReady = true;
          resolve();
          document.addEventListener("visitorConsentCollected", async () => {
            try {
              const v = await window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
              if (!v) {
                return;
              }
              this.setConsentGroup({ key: "personalization", checked: v.preferences === "yes" });
              this.setConsentGroup({ key: "marketing", checked: v.marketing === "yes" });
              this.setConsentGroup({ key: "analytics", checked: v.analytics === "yes" });
              this.saveSelections();
            } catch (err) {
              logger.error("visitorConsentCollected handler error", err);
            }
          });
        });
      } catch (e) {
        logger.error("Shopify loadFeatures threw", e);
        resolve();
      }
    });
  };
  /** Updates gtag consent, persists to localStorage, and fires `edition:consent-updated`. */
  applyConsent = ({ consent, hide }) => {
    window.gtag?.("consent", "update", {
      functionality_storage: "granted",
      analytics_storage: consent.analytics === "yes" ? "granted" : "denied",
      personalization_storage: consent.preferences === "yes" ? "granted" : "denied",
      ad_personalization: consent.preferences === "yes" ? "granted" : "denied",
      ad_storage: consent.marketing === "yes" ? "granted" : "denied",
      ad_user_data: consent.marketing === "yes" ? "granted" : "denied"
    });
    window.gtag?.("set", "ads_data_redaction", consent.marketing !== "yes");
    this.saveSelections();
    window.dispatchEvent(new CustomEvent("edition:consent-updated"));
    window.Shopify?.customerPrivacy?.setTrackingConsent?.({
      analytics: consent.analytics === "yes",
      marketing: consent.marketing === "yes",
      preferences: consent.preferences === "yes"
    });
    if (hide) {
      this.hideBanner();
    }
  };
  handleAllowAll = () => {
    this.querySelectorAll("input.ed-checkbox[data-consent]").forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = true;
      }
    });
    this.applyConsent({ consent: { preferences: "yes", marketing: "yes", analytics: "yes" }, hide: true });
  };
  handleAllowSelected = () => {
    const { personalization, marketing, analytics } = this.getSelections();
    this.applyConsent({
      consent: {
        preferences: personalization ? "yes" : "no",
        marketing: marketing ? "yes" : "no",
        analytics: analytics ? "yes" : "no"
      },
      hide: true
    });
  };
  handleRejectAll = () => {
    this.applyConsent({ consent: { preferences: "no", marketing: "no", analytics: "no" }, hide: true });
  };
  /** Keeps the consent/details tab panels in sync — the same consent key can appear in both tabs. */
  syncCheckboxGroups = () => {
    const checkboxes = Array.from(this.querySelectorAll("input.ed-checkbox[data-consent]"));
    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener("change", ({ currentTarget }) => {
        const source = currentTarget;
        checkboxes.forEach((other) => {
          if (other !== source && other.dataset.consent === source.dataset.consent && !other.disabled) {
            other.checked = source.checked;
          }
        });
      });
    });
    const grouped = checkboxes.reduce((acc, cb) => {
      const key = cb.dataset.consent;
      (acc[key] ??= []).push(cb);
      return acc;
    }, {});
    Object.values(grouped).forEach(([source, ...rest]) => {
      rest.forEach((cb) => {
        if (!cb.disabled) {
          cb.checked = source.checked;
        }
      });
    });
  };
  setConsentGroup = ({ key, checked }) => {
    this.querySelectorAll(`input.ed-checkbox[data-consent="${key}"]`).forEach((cb) => {
      if (!cb.disabled) {
        cb.checked = checked;
      }
    });
  };
  saveSelections = () => {
    local.set(CONSENT_STORAGE_KEY$1, this.getSelections());
  };
  getSelections = () => CONSENT_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: !!this.querySelector(`input.ed-checkbox[data-consent="${key}"]`)?.checked
    }),
    { personalization: false, marketing: false, analytics: false }
  );
  get overlay() {
    return this.querySelector("[data-cookie-overlay]") ?? this;
  }
  /**
   * Opens the banner. Applies any previously saved selections to the checkboxes first —
   * if nothing is stored, the HTML defaults remain as-is.
   * Can be triggered externally via `#manage-cookie-consent`.
   */
  showBanner = () => {
    const saved = local.get(CONSENT_STORAGE_KEY$1);
    if (saved) {
      CONSENT_KEYS.forEach((key) => this.setConsentGroup({ key, checked: saved[key] }));
    }
    this.overlay.removeAttribute("hidden");
    this.overlay.classList.remove("tw:hidden!");
  };
  hideBanner = () => {
    this.overlay.classList.add("tw:hidden!");
  };
  /** Creates a `window.gtag` shim that queues calls into `dataLayer` if the real gtag hasn't loaded yet. */
  ensureGtag = () => {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }
  };
}
const sha256 = async (value) => {
  if (!value) {
    return null;
  }
  try {
    const encoded = new TextEncoder().encode(value);
    const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
};
const resolveUrl = (input) => {
  if (typeof input === "string") {
    return input;
  }
  if (input instanceof URL) {
    return input.pathname;
  }
  return input.url;
};
const activeCurrency = () => window.Shopify?.currency?.active ?? "";
const parseRequestQuantity = (init) => {
  const body = init?.body;
  if (!body) {
    return 1;
  }
  try {
    if (typeof body === "string") {
      const parsed = JSON.parse(body);
      return Number(parsed.quantity) || 1;
    }
    if (body instanceof URLSearchParams) {
      return Number(body.get("quantity")) || 1;
    }
    if (body instanceof FormData) {
      return Number(body.get("quantity")) || 1;
    }
  } catch {
  }
  return 1;
};
const buildCartItem = (item, quantity) => ({
  item_name: item.product_title,
  item_id: String(item.product_id),
  item_brand: item.vendor,
  item_type: item.product_type,
  item_variant: String(item.variant_id),
  item_variant_name: item.variant_title,
  item_image: toAbsoluteUrl(item.featured_image?.url),
  item_url: toAbsoluteUrl(item.url),
  price: item.price / 100,
  sku: item.sku,
  quantity
});
const diffCart = ({ snapshot, newCart, onAddToCart, onRemoveFromCart }) => {
  const currency = activeCurrency();
  const snapshotMap = new Map(snapshot.items.map((item) => [item.key, item]));
  const newMap = new Map(newCart.items.map((item) => [item.key, item]));
  snapshotMap.forEach((oldItem, key) => {
    const newQty = newMap.get(key)?.quantity ?? 0;
    if (newQty < oldItem.quantity) {
      const qtyRemoved = oldItem.quantity - newQty;
      onRemoveFromCart({
        event: "edition_remove_from_cart",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency,
          value: oldItem.price / 100 * qtyRemoved,
          items: [buildCartItem(oldItem, qtyRemoved)]
        }
      });
    }
  });
  newMap.forEach((newItem, key) => {
    const oldQty = snapshotMap.get(key)?.quantity ?? 0;
    if (newItem.quantity > oldQty) {
      const qtyAdded = newItem.quantity - oldQty;
      onAddToCart({
        event: "edition_add_to_cart",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency,
          value: newItem.price / 100 * qtyAdded,
          items: [buildCartItem(newItem, qtyAdded)]
        }
      });
    }
  });
};
const initCartInterceptor = ({ onAddToCart, onRemoveFromCart }) => {
  const originalFetch = window.fetch.bind(window);
  const fetchCart = () => originalFetch("/cart.json").then((r) => r.json());
  window.fetch = async (input, init) => {
    const url = resolveUrl(input);
    if (url.includes("/cart/add")) {
      const response = await originalFetch(input, init);
      const addedQuantity = parseRequestQuantity(init);
      response.clone().json().then((data) => {
        const items = "items" in data ? data.items : [data];
        const currency = activeCurrency();
        items.forEach((item) => {
          onAddToCart({
            event: "edition_add_to_cart",
            ecommerce: {
              timestamp: (/* @__PURE__ */ new Date()).toISOString(),
              url: window.location.href,
              currency,
              value: item.price / 100 * addedQuantity,
              items: [
                {
                  item_name: item.product_title,
                  item_id: String(item.id),
                  item_brand: item.vendor ?? null,
                  item_type: item.product_type,
                  item_variant: String(item.id),
                  item_variant_name: item.variant_title,
                  item_image: item.featured_image?.url ?? null,
                  item_url: item.url,
                  price: item.price / 100,
                  sku: item.sku ?? null,
                  quantity: addedQuantity
                }
              ]
            }
          });
        });
      }).catch(() => {
      });
      return response;
    }
    if (url.includes("/cart/update") || url.includes("/cart/change")) {
      const snapshot = await fetchCart();
      const response = await originalFetch(input, init);
      const newCart = await fetchCart();
      diffCart({ snapshot, newCart, onAddToCart, onRemoveFromCart });
      return response;
    }
    return originalFetch(input, init);
  };
};
const DEBUG_MODE = true;
const CONSENT_STORAGE_KEY = "cookieSelections";
const DL_QUEUE = { KEY: "dl_pending_queue", TTL_MINUTES: 30, MAX_SIZE: 20 };
const PAGE_DATA_DEFAULTS = {
  pageType: "other",
  product: null,
  collection: null,
  searchQuery: null,
  customer: null
};
class DataLayer {
  /**
   * Registers the two window event listeners that drive the lifecycle.
   * Must be called once from `app.ts` — safe to call before `CookieBanner` connects.
   *
   * - `edition:consent-default-set` → runs full initialisation (once).
   * - `edition:consent-updated`     → flushes the session queue.
   */
  init = () => {
    window.addEventListener(
      "edition:consent-default-set",
      () => {
        this.initialize().catch((err) => logger.error("DataLayer init failed", err));
      },
      { once: true }
    );
    window.addEventListener("edition:consent-updated", () => {
      this.flushQueue();
    });
  };
  /** Runs the full initialisation sequence — pushes context, user, and page events then wires up interceptors. */
  initialize = async () => {
    window.dataLayer = window.dataLayer || [];
    const pageData = this.readPageData();
    this.pushLocation();
    this.pushContextData({ pageType: pageData.pageType });
    this.pushDeviceData();
    if (pageData.customer) {
      await this.pushUserData({ customer: pageData.customer });
    }
    await this.pushPageTypeEvent({ pageData });
    initCartInterceptor({
      onAddToCart: (payload) => this.pushToDataLayer({ payload, consentRequirement: "analytics" }),
      onRemoveFromCart: (payload) => this.pushToDataLayer({ payload, consentRequirement: "analytics" })
    });
    this.bindFormListeners();
    window.editionDataLayer = {
      trackCartView: this.trackCartView,
      trackAddToCart: this.trackAddToCart,
      trackRemoveFromCart: this.trackRemoveFromCart
    };
    {
      debugLog({ category: "init", label: "DataLayer initialized", payload: { pageType: pageData.pageType } });
    }
  };
  /** Parses the `#datalayer-data` JSON script tag injected by `datalayer-data.liquid`. Returns safe defaults on failure. */
  readPageData = () => {
    const el = document.getElementById("datalayer-data");
    if (!el?.textContent) {
      return PAGE_DATA_DEFAULTS;
    }
    try {
      return JSON.parse(el.textContent);
    } catch {
      logger.warn("Failed to parse #datalayer-data JSON");
      return PAGE_DATA_DEFAULTS;
    }
  };
  /**
   * Pushes a payload to `window.dataLayer` if the required consent is granted,
   * otherwise holds it in `sessionStorage` for later flushing.
   */
  pushToDataLayer = ({ payload, consentRequirement }) => {
    const isEvent = payload.event.startsWith("edition_");
    if (this.checkConsent({ requirement: consentRequirement })) {
      window.dataLayer.push(payload);
      {
        debugLog({ category: isEvent ? "event" : "context", label: payload.event, payload });
      }
    } else {
      this.queueEvent({ payload, consentRequirement });
      {
        debugLog({ category: "config", label: `Queued (no consent): ${payload.event}`, payload });
      }
    }
  };
  /** Reads consent state from localStorage — same key written by `CookieBanner`. */
  checkConsent = ({ requirement }) => {
    if (requirement === "none") {
      return true;
    }
    const selections = local.get(CONSENT_STORAGE_KEY);
    if (!selections) {
      return false;
    }
    if (requirement === "analytics") {
      return selections.analytics;
    }
    if (requirement === "marketing") {
      return selections.marketing;
    }
    return false;
  };
  /** Appends an event to the queue, dropping the oldest if the cap is reached. Persists for 30 minutes across page loads. */
  queueEvent = (event) => {
    const queue = this.getQueue();
    if (queue.length >= DL_QUEUE.MAX_SIZE) {
      queue.shift();
    }
    queue.push(event);
    local.set(DL_QUEUE.KEY, queue, DL_QUEUE.TTL_MINUTES);
  };
  /** Returns the current queue from localStorage, or an empty array if nothing is queued or TTL has expired. */
  getQueue = () => local.get(DL_QUEUE.KEY) ?? [];
  /**
   * Replays all queued events that now have consent, leaving the rest in place.
   * Called automatically on `edition:consent-updated`.
   */
  flushQueue = () => {
    const queue = this.getQueue();
    if (!queue.length) {
      return;
    }
    const remaining = [];
    queue.forEach(({ payload, consentRequirement }) => {
      if (this.checkConsent({ requirement: consentRequirement })) {
        window.dataLayer.push(payload);
        {
          debugLog({ category: "event", label: `Flushed: ${payload.event}`, payload });
        }
      } else {
        remaining.push({ payload, consentRequirement });
      }
    });
    local.set(DL_QUEUE.KEY, remaining, DL_QUEUE.TTL_MINUTES);
    const flushed = queue.length - remaining.length;
    if (flushed > 0 && DEBUG_MODE) {
      debugLog({ category: "init", label: `Flushed ${flushed} queued events`, payload: { remaining: remaining.length } });
    }
  };
  /** Pushes the current page URL breakdown and shop domain. No consent required. */
  pushLocation = () => {
    const { hash, host, hostname, href, origin, pathname, port, protocol, search } = window.location;
    this.pushToDataLayer({
      payload: {
        event: "location",
        location: { hash, host, hostname, href, origin, pathname, port, protocol, search },
        shop: window.Shopify?.shop ?? null
      },
      consentRequirement: "none"
    });
  };
  /** Pushes page type, title, and URL. No consent required. */
  pushContextData = ({ pageType }) => {
    this.pushToDataLayer({
      payload: {
        event: "contextData",
        page: { type: pageType, title: document.title, url: window.location.href }
      },
      consentRequirement: "none"
    });
  };
  /** Pushes browser/device signals — language, screen size, user agent, cookie state. No consent required. */
  pushDeviceData = () => {
    this.pushToDataLayer({
      payload: {
        event: "deviceData",
        encoding: document.characterSet,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
        screen: `${window.innerWidth}x${window.innerHeight}`,
        userAgent: navigator.userAgent
      },
      consentRequirement: "none"
    });
  };
  /** Hashes PII fields with SHA-256 before pushing — email, phone, first/last name. */
  pushUserData = async ({ customer }) => {
    const [sha256Email, sha256Phone, sha256FirstName, sha256LastName] = await Promise.all([
      customer.email ? sha256(customer.email.toLowerCase().trim()) : null,
      customer.phone ? sha256(customer.phone.replace(/\D/g, "")) : null,
      customer.first_name ? sha256(customer.first_name.toLowerCase().trim()) : null,
      customer.last_name ? sha256(customer.last_name.toLowerCase().trim()) : null
    ]);
    this.pushToDataLayer({
      payload: {
        event: "userData",
        customer: {
          email: customer.email ?? null,
          phone_number: customer.phone ?? null,
          sha256_email_address: sha256Email,
          sha256_phone_number: sha256Phone,
          address: customer.first_name ?? customer.last_name ? {
            first_name: customer.first_name ?? null,
            last_name: customer.last_name ?? null,
            sha256_first_name: sha256FirstName,
            sha256_last_name: sha256LastName
          } : null
        }
      },
      consentRequirement: "analytics"
    });
  };
  /**
   * Dispatches the page-type-specific event based on `pageData.pageType`.
   * For `search`, reads `?q` from the URL rather than relying on Liquid output.
   */
  pushPageTypeEvent = async ({ pageData }) => {
    switch (pageData.pageType) {
      case "product": {
        if (pageData.product) {
          this.pushViewItem({ product: pageData.product });
        }
        break;
      }
      case "collection": {
        if (pageData.collection) {
          this.pushCollectionView({ collection: pageData.collection });
        }
        break;
      }
      case "search": {
        const query = new URLSearchParams(window.location.search).get("q") ?? pageData.searchQuery;
        if (query) {
          this.pushSearch({ query });
        }
        break;
      }
      case "cart": {
        await this.trackCartView();
        break;
      }
      case "other": {
        break;
      }
      default: {
        const _exhaustive = pageData.pageType;
        logger.warn("Unhandled DataLayer page type", _exhaustive);
      }
    }
  };
  /** Pushes `edition_view_item` using the URL-selected variant, falling back to the first variant. Requires analytics consent. */
  pushViewItem = ({ product }) => {
    const urlVariantId = new URLSearchParams(window.location.search).get("variant");
    const variant = (urlVariantId ? product.variants.find((v) => String(v.id) === urlVariantId) : null) ?? product.variants[0];
    if (!variant) {
      return;
    }
    this.pushToDataLayer({
      payload: {
        event: "edition_view_item",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency: window.Shopify?.currency?.active ?? "",
          value: variant.price / 100,
          items: [
            {
              item_name: product.title,
              item_id: String(product.id),
              item_brand: product.vendor,
              item_type: product.type,
              item_variant: String(variant.id),
              item_variant_name: variant.title,
              item_image: toAbsoluteUrl(variant.featured_image?.src ?? product.featured_image),
              item_url: toAbsoluteUrl(`/products/${product.handle}`),
              price: variant.price / 100,
              sku: variant.sku ?? null,
              quantity: 1
            }
          ]
        }
      },
      consentRequirement: "analytics"
    });
  };
  /** Pushes `edition_collection_view` with all currently-rendered products (first 30 from Liquid). Requires analytics consent. */
  pushCollectionView = ({ collection }) => {
    const { title, handle, products } = collection;
    this.pushToDataLayer({
      payload: {
        event: "edition_collection_view",
        ecommerce: {
          timestamp: (/* @__PURE__ */ new Date()).toISOString(),
          url: window.location.href,
          currency: window.Shopify?.currency?.active ?? "",
          value: 1,
          item_list_name: title,
          item_list_id: handle,
          item_list_products_count: products.length,
          items: products.flatMap(
            (p) => p.variants.map(
              (v) => ({
                item_name: p.title,
                item_id: String(p.id),
                item_brand: p.vendor,
                item_type: p.type,
                item_variant: String(v.id),
                item_variant_name: v.title,
                item_image: toAbsoluteUrl(v.featured_image?.src ?? p.featured_image),
                item_url: toAbsoluteUrl(`/products/${p.handle}`),
                price: v.price / 100,
                sku: v.sku ?? null
              })
            )
          )
        }
      },
      consentRequirement: "analytics"
    });
  };
  /** Pushes `edition_search` with the query string only — no result products. Requires analytics consent. */
  pushSearch = ({ query }) => {
    this.pushToDataLayer({
      payload: {
        event: "edition_search",
        search_term: query,
        ecommerce: { timestamp: (/* @__PURE__ */ new Date()).toISOString(), url: window.location.href }
      },
      consentRequirement: "analytics"
    });
  };
  /** Fetches `/cart.json` and pushes `edition_cart_view`. Call this when a cart drawer opens. */
  trackCartView = async () => {
    try {
      const response = await fetch("/cart.json");
      const cart = await response.json();
      this.pushToDataLayer({
        payload: {
          event: "edition_cart_view",
          ecommerce: {
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            url: window.location.href,
            currency: cart.currency,
            value: cart.total_price / 100,
            items: cart.items.map(
              (item) => ({
                item_name: item.product_title,
                item_id: String(item.product_id),
                item_brand: item.vendor,
                item_type: item.product_type,
                item_variant: String(item.variant_id),
                item_variant_name: item.variant_title,
                item_image: toAbsoluteUrl(item.featured_image?.url),
                item_url: toAbsoluteUrl(item.url),
                price: item.price / 100,
                sku: item.sku,
                quantity: item.quantity
              })
            )
          }
        },
        consentRequirement: "analytics"
      });
    } catch (err) {
      logger.error("DataLayer: failed to fetch cart", err);
    }
  };
  /** Manually push an `edition_add_to_cart` event — use when a custom flow bypasses `/cart/add.js`. */
  trackAddToCart = (payload) => {
    this.pushToDataLayer({ payload, consentRequirement: "analytics" });
  };
  /** Manually push an `edition_remove_from_cart` event — use when a custom flow bypasses `/cart/update.js`. */
  trackRemoveFromCart = (payload) => {
    this.pushToDataLayer({ payload, consentRequirement: "analytics" });
  };
  /** Listens for contact form submits on `document` and distinguishes newsletter vs contact events by form fields. */
  bindFormListeners = () => {
    document.addEventListener("submit", (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      const action = form.getAttribute("action") ?? "";
      if (!action.includes("/contact")) {
        return;
      }
      const elements = Array.from(form.elements).filter((el) => el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement);
      const getField = (name) => elements.find((el) => el.name === name)?.value ?? null;
      const isNewsletter = elements.some((el) => el.name === "contact[tags]" && el instanceof HTMLInputElement && el.value === "newsletter");
      if (isNewsletter) {
        this.pushToDataLayer({
          payload: {
            event: "edition_newsletter_subscribe",
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            customer_email: getField("contact[email]")
          },
          consentRequirement: "analytics"
        });
      } else {
        this.pushToDataLayer({
          payload: {
            event: "edition_contact_form_submit",
            timestamp: (/* @__PURE__ */ new Date()).toISOString(),
            customer_name: getField("contact[Name]"),
            customer_email: getField("contact[email]"),
            customer_phone: getField("contact[Phone number]"),
            comment: getField("contact[Comment]")
          },
          consentRequirement: "analytics"
        });
      }
    });
  };
}
class Geolocator extends HTMLElement {
  geolocatorScript;
  countryDataArray;
  geolocatorContentTemplate;
  geolocatorContentPlaceholder;
  modal;
  closeModalButton;
  closeCountryListModalButton;
  countryModalToggle;
  countryListModal;
  countryListModalToggles;
  countryListButtons;
  stayButton;
  leaveButton;
  overlay;
  constructor() {
    super();
    this.getBrowsingContext = this.getBrowsingContext.bind(this);
  }
  async connectedCallback() {
    this.initDataAndElements();
    this.addEventListeners();
    this.handleGeolocationCookie();
    const isCookied = getCookie("geolocation");
    if (isCookied) {
      console.log("Geolocation cookie is set. Geolocator will not run.");
      return;
    }
    const browsingContext = await this.getBrowsingContext();
    const activeCountry = this.getActiveCountryAndLanguage();
    const recommendedMatch = this.getRecommendedMatch(browsingContext);
    const activeCountryHandle = activeCountry.country?.toUpperCase();
    const isAlreadyActive = recommendedMatch && (recommendedMatch.handle === activeCountry.country || recommendedMatch.countryCode === activeCountry.country || this.parseCountryList(recommendedMatch.marketCountries).includes(activeCountryHandle));
    if (isAlreadyActive) {
      console.log("Recommended match is the same as the active country. Geolocator will not run.");
      return;
    }
    this.toggleGeolocator();
    this.setupGeolocatorContent(recommendedMatch);
  }
  handleGeolocationCookie() {
    const urlParams = new URLSearchParams(window.location.search);
    const geolocation = urlParams.get("geolocation");
    if (geolocation) {
      setCookie("geolocation", geolocation, 30);
    }
  }
  toggleGeolocator() {
    this.classList.toggle("tw:hidden");
    this.setAttribute("geolocation-modal-active", this.getAttribute("geolocation-modal-active") === "true" ? "false" : "true");
    setTimeout(() => {
      this.modal.classList.toggle("active");
    }, 300);
  }
  setupGeolocatorContent(recommendedMatch) {
    const geolocatorContent = this.geolocatorContentTemplate.content.cloneNode(true);
    const content = geolocatorContent.querySelector(`[data-id="${recommendedMatch.id}"]`);
    if (content) {
      this.geolocatorContentPlaceholder.appendChild(content);
    }
    this.countryModalToggle = this.querySelector(".country-modal-toggle");
    this.stayButton = this.querySelector(".stay-button");
    this.leaveButton = this.querySelector(".leave-button");
    this.countryModalToggle?.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleCountryListModal(this.countryModalToggle);
    });
    this.stayButton.addEventListener("click", (event) => {
      event.preventDefault();
      setCookie("geolocation", recommendedMatch.handle, 30);
      this.toggleGeolocator();
    });
    this.leaveButton.addEventListener("click", (event) => {
      event.preventDefault();
      const { locale, handle, type } = this.leaveButton.dataset;
      setCookie("geolocation", handle ?? "", 30);
      const isMarket = type === "market";
      if (!isMarket) {
        window.location.href = `${window.location.href}?geolocation=${this.leaveButton.dataset.handle}`;
        return;
      }
      this.handleMarketSwitch(handle, locale);
    });
  }
  toggleCountryListModal(trigger) {
    const geoLocatorActive = this.getAttribute("geolocation-modal-active") === "true";
    const isInModalTrigger = trigger.dataset.inmodaltoggle === "true";
    if (isInModalTrigger && !geoLocatorActive) {
      this.classList.toggle("tw:hidden");
      this.countryListModal.classList.toggle("active");
      return;
    }
    if (isInModalTrigger) {
      this.modal.classList.toggle("active");
      this.countryListModal.classList.toggle("active");
    } else {
      this.classList.toggle("tw:hidden");
      this.countryListModal.classList.add("active");
    }
  }
  initDataAndElements() {
    this.modal = this.querySelector("#geolocator-modal");
    this.closeModalButton = this.querySelector("#close-geolocator-modal");
    this.geolocatorScript = this.querySelector("#geolocator-script");
    this.countryDataArray = JSON.parse(this.geolocatorScript.innerHTML);
    this.geolocatorContentTemplate = this.querySelector("#geolocator-content-tempalte");
    this.geolocatorContentPlaceholder = this.querySelector("#geolocator-content-placeholder");
    this.countryListModal = this.querySelector("#geolocator-countrylist-modal");
    this.closeCountryListModalButton = this.querySelector("#close-geolocator-countrylist-modal");
    this.countryListButtons = Array.from(this.querySelectorAll(".countrylist-button"));
    this.countryListModalToggles = Array.from(document.querySelectorAll(".country-modal-toggle"));
    this.overlay = this.querySelector(".geolocator-overlay");
  }
  addEventListeners() {
    this.closeModalButton?.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleGeolocator();
    });
    this.closeCountryListModalButton.addEventListener("click", (event) => {
      event.preventDefault();
      this.toggleCountryListModal(this.closeCountryListModalButton);
    });
    this.countryListButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.handleCountryListButtonClick(button);
      });
    });
    this.countryListModalToggles.forEach((toggle) => {
      toggle?.addEventListener("click", (event) => {
        event.preventDefault();
        this.toggleCountryListModal(toggle);
      });
    });
    this.overlay?.addEventListener("click", () => {
      this.closeAll();
    });
  }
  closeAll() {
    this.classList.add("tw:hidden");
    this.modal?.classList.remove("active");
    this.countryListModal?.classList.remove("active");
    this.setAttribute("geolocation-modal-active", "false");
  }
  handleCountryListButtonClick(button) {
    setCookie("geolocation", button.dataset.handle, 30);
    const isMarket = button.dataset.type === "market";
    if (!isMarket) {
      window.location.href = `${window.location.href}?geolocation=${button.dataset.handle}`;
      return;
    }
    const { locale, handle } = button.dataset;
    this.handleMarketSwitch(handle, locale);
  }
  handleMarketSwitch(handle, locale) {
    const block = this.countryDataArray.find((country) => country.handle === handle);
    const countryCode = block?.countryCode || handle;
    const formId = crypto.randomUUID();
    const formElement = document.createElement("form");
    formElement.id = formId;
    formElement.action = "/localization";
    formElement.method = "POST";
    formElement.hidden = true;
    formElement.innerHTML = `
      <input name="_method" value="PUT">
      <input name="country_code" value="${countryCode}">
      <input name="language_code" value="${locale}">
      <input name="return_to" value="${window.location.href}?geolocation=${handle}">
    `;
    document.body.appendChild(formElement);
    formElement.submit();
  }
  getActiveCountryAndLanguage() {
    const { country, locale } = window.Shopify;
    return {
      country,
      locale
    };
  }
  /**
   * Parse a comma-separated list of ISO country codes into an uppercased array.
   * Used for multi-country markets (e.g. EU) whose block covers several countries.
   */
  parseCountryList(value) {
    if (!value) return [];
    return value.split(",").map((code) => code.trim().toUpperCase()).filter(Boolean);
  }
  getRecommendedMatch(browsingContext) {
    if (!browsingContext) return;
    const { suggestedCountry, suggestedLanguage, detectedCountry } = browsingContext;
    const countryHandle = (suggestedCountry?.handle || detectedCountry?.handle)?.toUpperCase();
    const languageHandle = suggestedLanguage?.handle;
    if (!countryHandle) return;
    let recommendedMatch = this.countryDataArray.find((country) => country.handle === countryHandle && country.locale === languageHandle);
    if (!recommendedMatch) {
      recommendedMatch = this.countryDataArray.find((country) => country.handle === countryHandle);
    }
    if (!recommendedMatch) {
      recommendedMatch = this.countryDataArray.find((country) => this.parseCountryList(country.marketCountries).includes(countryHandle));
    }
    if (!recommendedMatch) {
      recommendedMatch = this.countryDataArray.find((country) => country.primary);
    }
    return recommendedMatch;
  }
  async getBrowsingContext() {
    const response = await fetch("/browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true");
    const data = await response.json();
    const detectedValues = data.detected_values;
    const { handle, name } = detectedValues.country;
    const { suggestions } = data;
    let suggestedCountry = null;
    let suggestedLanguage = null;
    if (suggestions.length) {
      const suggestion = suggestions.find((_suggestion) => _suggestion.parts.country.handle === detectedValues.country.handle);
      suggestedCountry = suggestion?.parts.country;
      suggestedLanguage = suggestion?.parts.language;
    }
    const browsingContext = {
      detectedCountry: {
        handle,
        name
      },
      suggestedCountry,
      suggestedLanguage
    };
    return browsingContext;
  }
}
const scripts = new ThemeScripts();
const dataLayer = new DataLayer();
scripts.load({
  id: "gtm",
  strategy: "on-default-consent-set",
  gtmConfig: { containerId: "GTM-XXXXXX" }
});
dataLayer.init();
customElements.define("cookie-banner", CookieBanner);
customElements.define("geolocator-component", Geolocator);
